/**
 * Scrolling or arrow-keying a numeric field must never change its value.
 *
 * A focused number input treats the mouse wheel and the Up/Down arrows as stepper input, so
 * scrolling a page with the pointer over a Pay Rate or Markup field silently rewrote financial
 * data. The guard is installed once for the whole application rather than per field: of the 92
 * number inputs here, 76 go through the design system's Input, and the remaining 16 are raw
 * styled.input elements, MUI TextFields and MUI DataGrid number editors that no shared component
 * can reach.
 *
 * Wheel is handled by BLURRING rather than by preventDefault, so the page still scrolls -
 * preventDefault would block the scroll along with the increment. Blurring synchronously during
 * the capture phase means the field is no longer focused by the time the browser applies its
 * default action, so the value holds. It also keeps the listener passive, so scrolling stays
 * smooth.
 *
 * Arrow keys are handled by preventDefault, which stops the step without touching focus.
 */

const isNumberInput = (node: EventTarget | null): node is HTMLInputElement =>
  node instanceof HTMLInputElement && node.type === 'number'

export function installNumericInputGuards(doc: Document = document): () => void {
  const handleWheel = (event: WheelEvent) => {
    const focused = doc.activeElement
    if (!isNumberInput(focused)) return
    /* Only when the pointer is actually over the focused field. That is the only case the
       browser treats as stepper input, and blurring on every scroll anywhere on the page would
       yank focus out from under someone who is mid-edit somewhere else. */
    if (event.target !== focused) return
    focused.blur()
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
    if (!isNumberInput(event.target)) return
    event.preventDefault()
  }

  doc.addEventListener('wheel', handleWheel, {capture: true, passive: true})
  doc.addEventListener('keydown', handleKeyDown, true)

  return () => {
    doc.removeEventListener('wheel', handleWheel, true)
    doc.removeEventListener('keydown', handleKeyDown, true)
  }
}
